import * as d3 from "d3";

interface RechargeElement {
  annee: number;
  valeur: number;
}

interface OptionsHistogramme {
  container: string;  // Container selector
  data: RechargeElement[];
  normale: number | null;
  margin?: { top: number; right: number; bottom: number; left: number };
}

export class HistogrammeD3 {
  private container: HTMLElement;
  private data: RechargeElement[];
  private normale: number | null = null;
  private margin = { top: 40, right: 20, bottom: 40, left: 50 };
  private svg!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private tooltip!: d3.Selection<HTMLDivElement, unknown, null, undefined>;
  private width!: number;
  private height!: number;

  constructor(options: OptionsHistogramme) {
    this.container = document.querySelector(options.container)!;
    this.data = options.data;
    this.normale = options.normale;
    if (options.margin) this.margin = options.margin;

    this.init();
    this.render();

    window.addEventListener("resize", () => this.onResize());
  }

  private init() {
    d3.select(this.container).select("svg").remove();

    this.svg = d3
      .select(this.container)
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%");

    d3.select(this.container).selectAll(".recharge-tooltip").remove();

    this.tooltip = d3.select(this.container)
      .append("div")
      .attr("class", "recharge-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "rgba(0, 0, 0, 0.8)")
      .style("color", "white")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("z-index", "1000")
  }

  private getDimensions() {
    const rect = this.container.getBoundingClientRect();
    this.width = rect.width - this.margin.left - this.margin.right;
    this.height = rect.height - this.margin.top - this.margin.bottom;
  }

  private render() {
    this.getDimensions();

    // Cleaning (SVG et tooltip)
    this.svg.selectAll("*").remove();

    // Main group with margins
    const g = this.svg
      .append("g")
      .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    // Scales
    const x = d3
      .scaleBand<number>()
      .domain(this.data.map((d) => d.annee))
      .range([0, this.width])
      .padding(0.1);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(this.data, (d) => d.valeur)!])
      .nice()
      .range([this.height, 0]);

    // Axis
    const xAxis = d3.axisBottom(x)

    const yAxis = d3.axisLeft(y)

    g.append("g")
      .attr("transform", `translate(0,${this.height})`)
      .call(xAxis)
      .selectAll("text")
      .style("font-size", "12px")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", "-.3em")
      .attr("transform", function (d) {
        return "rotate(-90)";
      });

    // Y scale unit
    g.append("text")
      .attr("x", -40)
      .attr("y", -20)
      .style("text-anchor", "left")
      .text("Recharge de la nappe en millimètres/an");

    g.append("g").call(yAxis)
      .selectAll("text")
      .style("font-size", "12px");

    // Bars
    g.selectAll(".bar")
      .data(this.data)
      .join("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.annee)!)
      .attr("y", (d) => y(d.valeur))
      .attr("width", x.bandwidth())
      .attr("height", (d) => this.height - y(d.valeur))
      .attr("fill", (d) => (this.normale != null && d.valeur > this.normale!) ? "#4e79a7" : "#a74e4eff")
      .on("mouseover", (event, d) => {
        this.tooltip
          .style("visibility", "visible")
          .html(`<strong>Année:</strong> ${d.annee}<br/><strong>Valeur:</strong> ${d.valeur.toFixed(2)} mm`);
      })
      .on("mousemove", (event) => {
        const containerRect = this.container.getBoundingClientRect();
        const tooltipNode = this.tooltip.node() as HTMLElement;
        const tooltipRect = tooltipNode.getBoundingClientRect();

        let x = event.clientX - containerRect.left + 10;
        let y = event.clientY - containerRect.top - 40;

        // Vérifier si la tooltip dépasse à droite
        if (x + tooltipRect.width > containerRect.width) {
          x = event.clientX - containerRect.left - tooltipRect.width + 10;
        }

        this.tooltip
          .style("top", y + "px")
          .style("left", x + "px");
      })
      .on("mouseout", () => {
        this.tooltip.style("visibility", "hidden");
      });

    if (this.normale !== null) {
      g.append("line")
        .attr("x1", 0)
        .attr("x2", this.width)
        .attr("y1", y(this.normale))
        .attr("y2", y(this.normale))
        .attr("stroke", "#67a74eff")
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", "5,5");

      // Legend
      const legend = g.append("g")
        .attr("class", "legend")
        .attr("transform", `translate(${this.width - 300}, -20)`);

      legend.append("line")
        .attr("x1", 0)
        .attr("x2", 30)
        .attr("y1", 0)
        .attr("y2", 0)
        .attr("stroke", "#67a74eff")
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", "5,5");

      legend.append("text")
        .attr("x", 35)
        .attr("y", 0)
        .attr("dy", "0.35em")
        .style("font-size", "12px")
        .style("fill", "#333")
        .text(`Valeur normale (1991-2020) : ${this.normale.toFixed(2)} mm`);
    }
  }

  private onResize() {
    this.render();
  }

  public updateData(data: RechargeElement[]) {
    this.data = data;
    this.render();
  }
}
